import { CharCount } from "../../../../molecules/char-count";
import { ApplicationsConst } from "../../../../../application/domain/application/const";
import { formatDatetime } from "../../../../../utils/date-utils";
import * as React from "react";
import { useEffect, useState } from "react";
import apiClient from "../../../../../application/apis/api-client";
import {
  SmartApplyApiError,
  reportUnexpectedError,
} from "@graffer-inc/smart-apply";
import { toast } from "react-toastify";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "@graffer-inc/smart-apply";
import { InternalCommentsAndUsersQuery } from "@graffer-inc/smart-apply";
import { GrafferGqlResult } from "@graffer-inc/smart-apply";
import { SecondaryButton } from "@graffer-inc/console-design-kit";

export const InternalComments: React.FC<{
  applicationId: string;
  refreshApplication: (id: string) => Promise<void>;
}> = ({ applicationId, refreshApplication }) => {
  const [editingComment, setEditingComment] = useState("");
  const [internalComments, setInternalComments] = useState<
    GrafferGqlResult<InternalCommentsAndUsersQuery>
  >(GrafferGqlResult.init());

  const fetchInternalComments = React.useCallback(
    async (applicationId: string) => {
      const result = await runGqlQueryV2(() =>
        gqlClient.internalCommentsAndUsers(
          { id: applicationId },
          gqlHeader("q"),
        ),
      );
      setInternalComments(result);
      setEditingComment("");
    },
    [setInternalComments],
  );

  const addComment = React.useCallback(
    async (content: string) => {
      try {
        await apiClient.addInternalComment(applicationId, {
          content,
        });
        await fetchInternalComments(applicationId);
        await refreshApplication(applicationId);
      } catch (e) {
        if (SmartApplyApiError.isRetryable(e)) {
          toast.error(
            "ネットワークエラーが発生しました。もう一度コメントを追加してください。",
          );
        }
        if (SmartApplyApiError.is409(e)) {
          toast.error(
            "内部コメントの追加に失敗しました。画面を更新してから再度コメントを追加してください。",
          );
        }
        toast.error(
          "予期しないエラーが発生しました。時間をおいてから、もう一度コメントを追加してください。",
        );
        reportUnexpectedError(e);
      }
    },
    [applicationId, fetchInternalComments, refreshApplication],
  );

  const numOfCharsDiff =
    ApplicationsConst.INTERNAL_COMMENT_MAX_LENGTH - editingComment.length;

  useEffect(() => {
    fetchInternalComments(applicationId).catch((err) => {
      if (SmartApplyApiError.isRetryable(err)) {
        fetchInternalComments(applicationId).catch((err) =>
          reportUnexpectedError(err),
        );
        return;
      }
      reportUnexpectedError(err);
    });
  }, [applicationId, fetchInternalComments]);

  return (
    <nav className="card">
      <div className="card-content">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            addComment(editingComment);
          }}
        >
          <div className="field">
            <label htmlFor="comment" className="label">
              内部管理用コメント
            </label>
            <div className="control">
              <textarea
                className="textarea"
                value={editingComment}
                onChange={(v) => setEditingComment(v.target.value)}
              />
            </div>
            <CharCount
              text={editingComment}
              maxLength={ApplicationsConst.INTERNAL_COMMENT_MAX_LENGTH}
            />
          </div>
          <div className="field">
            <div className={"has-text-centered"}>
              <SecondaryButton
                text={"コメントを登録"}
                disabled={
                  !!internalComments.error ||
                  !editingComment ||
                  editingComment.length === 0 ||
                  numOfCharsDiff < 0
                }
                type="submit"
              />
            </div>
          </div>
        </form>
      </div>
      <div className="card-content is-fullwidth">
        {internalComments.matchResult(
          {
            rootPath: "internalCommentsAndUsers",
            errorTypes: "forbidden",
          },
          {
            forbidden: (
              <div className="notification is-warning is-light">
                権限がないため、コメントを表示できませんでした。
              </div>
            ),
            loading: undefined,
            unexpected: (
              <div className="notification is-warning is-light">
                予期しないエラーが発生したため、
                コメントを表示できませんでした。
              </div>
            ),
            success: (comments): any => {
              return (
                <>
                  {(comments.internalCommentsAndUsers || []).map((c, index) => {
                    return (
                      <article className="media" key={index}>
                        <div className="media-content">
                          <div className="content">
                            <p>
                              {c.createdBy && (
                                <strong className="mr-1">
                                  {c.createdBy.name}
                                </strong>
                              )}
                              <small>{formatDatetime(c!.createdAt)}</small>
                              <br />
                              {c.content}
                            </p>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </>
              );
            },
          },
        )}
      </div>
    </nav>
  );
};
